import { useRef, useState } from "react"

// ** Third Party Components
import * as z from "zod"
import axios from "axios"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  CalendarDaysIcon,
  ChevronsUpDown,
  Eye,
  FileText,
  Info,
  Loader,
  Upload,
  X,
} from "lucide-react"

// ** Internal Elements
import { Input } from "@components/common/input"
import { Label } from "@components/common/label"
import { Switch } from "@components/common/switch"
import { Button } from "@components/common/button"
import { Heading } from "@components/common/heading"
import { Textarea } from "@components/common/textarea"
import { Calendar } from "@components/common/calendar"
import { Separator } from "@components/common/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/common/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"

// ** Types
import { DictType } from "@pma/types"

// ** Constants
import { ACCESS_TOKEN, BLOCKED_EXTENSIONS } from "@pma/utils/constants"

// ** API routes
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import {
  CREATE_PROJECT_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT,
  NEIGHBORHOOD_DICTIONARY_ENDPOINT,
  PROJECT_TYPES_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useModal } from "@stores/modal-store"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import GeoCoder from "./geo-coder"
import AddNeighborhoodForm from "./add-neighborhood-form"
import ProjectStatusEditor from "./project-status-editor"
import ProjectWorkTypeEditor from "./project-work-type-editor"
import { ProjectFormValues, projectFormSchema } from "./projects-form-schema"

const AddProjectModal = () => {
  const modal = useModal()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const userId = authConfig?.id
  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const newParams = new URLSearchParams(searchParam)
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const [neighborhoodOpen, setNeighborhoodOpen] = useState(false)
  const [previewColId, setPreviewColId] = useState<string | null>(null)
  const [fileUploading, setFileUploading] = useState<string | null>(null)
  const [customColumnValues, setCustomColumnValues] = useState<Record<string, string>>({})
  const [fileUploads, setFileUploads] = useState<Record<string, { url: string; name: string }>>({})

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {},
    mode: "onChange",
  })

  const getNeighborhoods = async () => {
    const response = await axios.get(SERVER_URL + NEIGHBORHOOD_DICTIONARY_ENDPOINT, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: neighborhoods, refetch: refetchNeighborhoods } = useQuery({
    queryKey: ["neighborhoods", orgId],
    queryFn: getNeighborhoods,
    enabled: modal.isOpen && modal.context === "project",
  })

  const getProjectTypes = async () => {
    const response = await axios.get(SERVER_URL + PROJECT_TYPES_DICTIONARY_ENDPOINT, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })

    return response.data
  }

  const { data: projectTypes } = useQuery({
    queryKey: ["project-types", orgId],
    queryFn: getProjectTypes,
    enabled: modal.isOpen && modal.context === "project",
  })

  const getProjectCustomColumns = async () => {
    const response = await axios.get(SERVER_URL + GET_PROJECT_CUSTOM_COLUMNS_ENDPOINT, {
      params: { organization_id: orgId },
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const { data: projectCustomColumns = [] } = useQuery({
    queryKey: ["project-custom-columns", orgId],
    queryFn: getProjectCustomColumns,
    enabled: modal.isOpen && modal.context === "project" && !!orgId,
  })

  const addProject = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + CREATE_PROJECT_ENDPOINT,
      { ...payload },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const addProjectMutation = useMutation({
    mutationKey: ["add-project"],
    mutationFn: addProject,
    onSuccess: (data) => {
      form.reset()
      setCustomColumnValues({})
      setFileUploads({})
      setPreviewColId(null)

      // Trigger Revalidate projects
      newParams.set("revalidate", "projects")

      navigate(PROJECTS_PAGE_ROUTE + `/${data?.id}?${newParams.toString()}`)
      modal.onClose()
      modal.setContext(undefined)
      toast({
        title: translate("Success"),
        description: translate("Project added successfully"),
        variant: "success",
      })
    },

    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data?.message || translate("Unable to create project"),
        variant: "error",
      })
    },
  })

  const onSubmit = async (data: z.infer<typeof projectFormSchema>) => {
    const startDate = data?.start_at ? new Date(data?.start_at).getTime() : 0
    const endData = data?.finish_to ? new Date(data?.finish_to).getTime() : 0
    const selectedProjectType = data?.type_id?.toLocaleLowerCase()

    const projectType = projectTypes.filter((type: DictType) => {
      if (isHebrew) {
        return type.project_type.toLocaleLowerCase() === selectedProjectType
      } else {
        return type.project_type_en.toLocaleLowerCase() === selectedProjectType
      }
    })

    const customColumns = Object.entries(customColumnValues)
      .filter(([_, value]) => value !== "")
      .map(([columnId, value]) => {
        const col = projectCustomColumns?.find((c: DictType) => String(c.id) === columnId)
        const colType = col?.type?.toLowerCase()
        let finalValue: string | number = value
        if (colType === "timestamp") {
          finalValue = Math.floor(new Date(value).getTime() / 1000)
        }
        return { column_id: Number(columnId), value: String(finalValue) }
      })

    const payload: DictType = {
      owner_id: userId,
      organization_user_id: orgId,
      title: data?.title,
      description: data?.description?.trim() ? data?.description : undefined,
      status_id: data?.status?.id,
      type_id: projectType[0]?.id,
      start_at: startDate,
      finish_to: endData,
      address_text: data?.address?.name,
      neighborhood_id: data?.neighborhood?.id,
      construction_name: data?.construction_name?.trim() ? data?.construction_name : undefined,
      point_lng: data?.address?.long,
      point_lat: data?.address?.lat,
      work_type_id: data?.work_type_id,
      use_gis: !!data?.use_gis,
    }

    if (customColumns.length > 0) {
      payload.custom_columns = customColumns
    }

    addProjectMutation.mutate({ ...payload })
  }

  const closeModal = () => {
    form.reset()
    setCustomColumnValues({})
    setFileUploads({})
    setPreviewColId(null)
    modal.onClose()
    modal.setContext(undefined)
    return
  }

  const gisValue = form.watch("use_gis")

  return (
    <Dialog open={modal.isOpen && modal.context === "project"} onOpenChange={closeModal}>
      <DialogContent className={cn("max-w-[700px]", isHebrew ? "rtl" : "ltr")}>
        <DialogHeader>
          <DialogTitle className={cn(isHebrew ? "text-right" : "text-left")}>
            <Heading
              title={translate(`Create new project`)}
              description={translate("Fill the details to proceed")}
            />
          </DialogTitle>
        </DialogHeader>
        <div className='grid h-[550px] gap-4 overflow-y-scroll'>
          <Separator />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className='container px-4 md:px-6'>
                <div className='mx-auto max-w-2xl space-y-6'>
                  <div className='grid grid-cols-2 gap-6'>
                    <FormField
                      control={form.control}
                      name='title'
                      render={({ field }) => (
                        <FormItem className='capitalize'>
                          <FormLabel className='capitalize'>
                            {translate("Title")}
                            <span className='text-destructive'>*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Enter Title")}
                              className='text-black'
                              {...field}
                              disabled={addProjectMutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className='col-span-1 w-full space-y-2'>
                      <FormField
                        control={form.control}
                        name='neighborhood'
                        render={({ field }) => (
                          <FormItem className='w-full capitalize'>
                            <FormLabel>{translate("Neighborhood")}</FormLabel>
                            <Popover open={neighborhoodOpen} onOpenChange={setNeighborhoodOpen}>
                              <FormControl>
                                <PopoverTrigger
                                  disabled={addProjectMutation.isPending}
                                  className='flex h-9 w-full items-center justify-between rounded-md border p-1.5 text-sm'
                                >
                                  <div className=''>
                                    {field?.value?.name ? (
                                      <div className='w-[23ch] overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize'>
                                        {field?.value?.name}
                                      </div>
                                    ) : (
                                      translate("Select Neighborhood")
                                    )}
                                  </div>
                                  <ChevronsUpDown size={12} />
                                </PopoverTrigger>
                              </FormControl>
                              <PopoverContent className='w-full p-0'>
                                <Command>
                                  <CommandInput
                                    placeholder={translate("Search neighborhood") + "..."}
                                  />
                                  <CommandList>
                                    <CommandEmpty>{translate("No status found")}.</CommandEmpty>
                                    <CommandGroup>
                                      {neighborhoods?.map(
                                        (neighborhood: DictType, index: number) => (
                                          <CommandItem
                                            key={neighborhood.id + index}
                                            value={neighborhood.id}
                                            onSelect={() => {
                                              field.onChange({
                                                name: neighborhood?.name,
                                                id: neighborhood?.id,
                                              })
                                              setNeighborhoodOpen(false)
                                            }}
                                            className={cn(
                                              "my-1 capitalize",
                                              field?.value?.name === neighborhood.name &&
                                                "bg-secondary/30 text-black",
                                            )}
                                          >
                                            {neighborhood.name}
                                          </CommandItem>
                                        ),
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                                <div className='px-2 pb-2'>
                                  <Separator />
                                  <AddNeighborhoodForm refetch={refetchNeighborhoods} />
                                </div>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-6'>
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name={`work_type_id`}
                        render={({ field }) => (
                          <FormItem className='capitalize'>
                            <FormLabel>{translate("Work Type")}</FormLabel>
                            <FormControl>
                              <ProjectWorkTypeEditor setWorkType={field.onChange} />
                            </FormControl>

                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name='status'
                        render={({ field }) => (
                          <FormItem className=''>
                            <FormLabel className='capitalize'>{translate("Status")}</FormLabel>
                            <FormControl>
                              <ProjectStatusEditor setStatus={field.onChange} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-6'>
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name={`use_gis`}
                        render={({ field }) => (
                          <FormItem className=''>
                            <FormLabel>{translate("Support GIS")}</FormLabel>
                            <FormControl>
                              <div className='flex gap-6'>
                                <Switch
                                  checked={field.value}
                                  className='h-6 w-12'
                                  onCheckedChange={field.onChange}
                                />
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Info size={16} className='text-slate-500' />
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {translate("If set to True, the project will support GIS data")}
                                  </TooltipContent>
                                </Tooltip>{" "}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name={`address`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={cn("capitalize", !gisValue && "text-gray-500")}>
                              {translate("Address")}
                              {gisValue && <span className='text-destructive'>*</span>}
                            </FormLabel>
                            <GeoCoder
                              disabled={!gisValue}
                              location={field?.value?.name || ""}
                              setGeoLocation={field.onChange}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-2 gap-6'>
                    <div className='grid grid-cols-2 gap-6'>
                      <div className='col-span-2 space-y-2'>
                        <FormItem className='flex-1'>
                          <FormLabel>{translate("Date Range")}</FormLabel>
                          <FormControl>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  className='w-full justify-start text-left font-normal'
                                  variant='outline'
                                >
                                  <CalendarDaysIcon
                                    className={cn(
                                      "h-4 w-4",
                                      isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                    )}
                                  />
                                  {(() => {
                                    const startAt = form.watch("start_at")
                                    const finishTo = form.watch("finish_to")

                                    if (startAt && finishTo) {
                                      return (
                                        <span>
                                          {format(startAt, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                          {" - "}
                                          {format(finishTo, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                        </span>
                                      )
                                    }

                                    if (startAt) {
                                      return (
                                        <span>
                                          {format(startAt, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                          {" - "}
                                          {translate("Select end date")}
                                        </span>
                                      )
                                    }

                                    return <span>{translate("Select Date Range")}</span>
                                  })()}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent align='start' className='w-auto p-0'>
                                <Calendar
                                  mode='range'
                                  selected={{
                                    from: form.watch("start_at"),
                                    to: form.watch("finish_to"),
                                  }}
                                  onSelect={(range) => {
                                    form.setValue("start_at", range?.from)
                                    form.setValue("finish_to", range?.to)
                                  }}
                                  locale={isHebrew ? he : undefined}
                                  numberOfMonths={2}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </div>
                    </div>
                  </div>
                  <div className='grid gap-6'>
                    <div className='space-y-2'>
                      <FormField
                        control={form.control}
                        name='description'
                        render={({ field }) => (
                          <FormItem className='space-y-1'>
                            <FormLabel>{translate("Description")}</FormLabel>
                            <FormControl>
                              <Textarea
                                className='min-h-[150px] flex-1 resize-none'
                                placeholder={translate(`Enter project description`)}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Custom Columns */}
                  {projectCustomColumns?.length > 0 && (
                    <>
                      <Separator />
                      <Label className='text-sm font-semibold'>{translate("Custom Columns")}</Label>
                      <div className='grid grid-cols-2 gap-6'>
                        {projectCustomColumns.map((col: DictType) => {
                          const colType = col?.type?.toLowerCase()
                          const colId = String(col?.id)
                          const colValue = customColumnValues[colId] ?? ""

                          const updateValue = (val: string) => {
                            setCustomColumnValues((prev) => ({ ...prev, [colId]: val }))
                          }

                          if (colType === "boolean" || colType === "checkbox") {
                            return (
                              <div className='space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <div>
                                  <Switch
                                    checked={colValue === "true"}
                                    onCheckedChange={(checked) => updateValue(String(checked))}
                                  />
                                </div>
                              </div>
                            )
                          }

                          if (colType === "selector") {
                            const options: string[] = col?.list || []
                            return (
                              <div className='space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <Select value={colValue} onValueChange={updateValue}>
                                  <SelectTrigger className={cn("w-full", isHebrew ? "rtl" : "ltr")}>
                                    <SelectValue placeholder={translate("Select")} />
                                  </SelectTrigger>
                                  <SelectContent className={cn(isHebrew ? "rtl" : "ltr")}>
                                    {options.map((item) => (
                                      <SelectItem value={item} key={item}>
                                        {item}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )
                          }

                          if (colType === "date") {
                            const selectedDate = colValue ? new Date(colValue) : undefined
                            return (
                              <div className='space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      className='w-full justify-start text-left font-normal'
                                      variant='outline'
                                    >
                                      <CalendarDaysIcon
                                        className={cn(
                                          "h-4 w-4",
                                          isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                        )}
                                      />
                                      {selectedDate ? (
                                        format(selectedDate, "PP", {
                                          locale: isHebrew ? he : undefined,
                                        })
                                      ) : (
                                        <span>{translate("Select Date")}</span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent align='start' className='w-auto p-0'>
                                    <Calendar
                                      mode='single'
                                      locale={isHebrew ? he : undefined}
                                      selected={selectedDate}
                                      onSelect={(date) => {
                                        if (date) {
                                          const y = date.getFullYear()
                                          const m = String(date.getMonth() + 1).padStart(2, "0")
                                          const d = String(date.getDate()).padStart(2, "0")
                                          updateValue(`${y}-${m}-${d}`)
                                        }
                                      }}
                                    />
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )
                          }

                          if (colType === "timestamp") {
                            const tsDate = colValue ? new Date(colValue) : undefined
                            return (
                              <div className='space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      className='w-full justify-start text-left font-normal'
                                      variant='outline'
                                    >
                                      <CalendarDaysIcon
                                        className={cn(
                                          "h-4 w-4",
                                          isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                        )}
                                      />
                                      {tsDate ? (
                                        format(tsDate, "PP p", {
                                          locale: isHebrew ? he : undefined,
                                        })
                                      ) : (
                                        <span>{translate("Select Date")}</span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent align='start' className='w-auto p-2'>
                                    <Calendar
                                      mode='single'
                                      locale={isHebrew ? he : undefined}
                                      selected={tsDate}
                                      onSelect={(date) => {
                                        if (date) {
                                          const existing = tsDate || new Date()
                                          date.setHours(existing.getHours(), existing.getMinutes())
                                          updateValue(date.toISOString())
                                        }
                                      }}
                                    />
                                    <div className='flex items-center justify-center gap-2 border-t pt-2'>
                                      <input
                                        type='time'
                                        className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                                        value={
                                          tsDate
                                            ? `${String(tsDate.getHours()).padStart(2, "0")}:${String(tsDate.getMinutes()).padStart(2, "0")}`
                                            : ""
                                        }
                                        onChange={(e) => {
                                          const [hours, minutes] = e.target.value
                                            .split(":")
                                            .map(Number)
                                          const current = tsDate ? new Date(tsDate) : new Date()
                                          current.setHours(hours, minutes)
                                          updateValue(current.toISOString())
                                        }}
                                      />
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )
                          }

                          if (colType === "range") {
                            let rangeFrom: Date | undefined
                            let rangeTo: Date | undefined
                            try {
                              const parsed = colValue ? JSON.parse(colValue) : {}
                              rangeFrom = parsed.from
                                ? new Date(Number(parsed.from) * 1000)
                                : undefined
                              rangeTo = parsed.to ? new Date(Number(parsed.to) * 1000) : undefined
                            } catch {
                              // ignore parse errors
                            }

                            return (
                              <div className='col-span-2 space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                                      className='w-full justify-start text-left font-normal'
                                      variant='outline'
                                    >
                                      <CalendarDaysIcon
                                        className={cn(
                                          "h-4 w-4",
                                          isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                        )}
                                      />
                                      {rangeFrom && rangeTo ? (
                                        <span>
                                          {format(rangeFrom, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                          {" - "}
                                          {format(rangeTo, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                        </span>
                                      ) : rangeFrom ? (
                                        <span>
                                          {format(rangeFrom, "PP", {
                                            locale: isHebrew ? he : undefined,
                                          })}
                                          {" - "}
                                          {translate("Select end date")}
                                        </span>
                                      ) : (
                                        <span>{translate("Select Date Range")}</span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent align='start' className='w-auto p-2'>
                                    <Calendar
                                      mode='range'
                                      locale={isHebrew ? he : undefined}
                                      selected={{ from: rangeFrom, to: rangeTo }}
                                      onSelect={(range) => {
                                        if (!range) return
                                        updateValue(
                                          JSON.stringify({
                                            from: range.from
                                              ? Math.floor(range.from.getTime() / 1000)
                                              : "",
                                            to: range.to
                                              ? Math.floor(range.to.getTime() / 1000)
                                              : "",
                                          }),
                                        )
                                      }}
                                      numberOfMonths={2}
                                    />
                                    <div className='flex items-center justify-center gap-4 border-t pt-2'>
                                      <div className='flex items-center gap-1'>
                                        <label className='text-xs text-muted-foreground'>
                                          {translate("From")}:
                                        </label>
                                        <input
                                          type='time'
                                          className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                                          value={
                                            rangeFrom
                                              ? `${String(rangeFrom.getHours()).padStart(2, "0")}:${String(rangeFrom.getMinutes()).padStart(2, "0")}`
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const [h, m] = e.target.value.split(":").map(Number)
                                            const d = rangeFrom ? new Date(rangeFrom) : new Date()
                                            d.setHours(h, m)
                                            const toTs = rangeTo
                                              ? Math.floor(rangeTo.getTime() / 1000)
                                              : ""
                                            updateValue(
                                              JSON.stringify({
                                                from: Math.floor(d.getTime() / 1000),
                                                to: toTs,
                                              }),
                                            )
                                          }}
                                        />
                                      </div>
                                      <div className='flex items-center gap-1'>
                                        <label className='text-xs text-muted-foreground'>
                                          {translate("To")}:
                                        </label>
                                        <input
                                          type='time'
                                          className='rounded border px-2 py-1 text-sm outline-none focus:border-primary'
                                          value={
                                            rangeTo
                                              ? `${String(rangeTo.getHours()).padStart(2, "0")}:${String(rangeTo.getMinutes()).padStart(2, "0")}`
                                              : ""
                                          }
                                          onChange={(e) => {
                                            const [h, m] = e.target.value.split(":").map(Number)
                                            const d = rangeTo ? new Date(rangeTo) : new Date()
                                            d.setHours(h, m)
                                            const fromTs = rangeFrom
                                              ? Math.floor(rangeFrom.getTime() / 1000)
                                              : ""
                                            updateValue(
                                              JSON.stringify({
                                                from: fromTs,
                                                to: Math.floor(d.getTime() / 1000),
                                              }),
                                            )
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            )
                          }

                          if (colType === "file") {
                            const fileData = fileUploads[colId]
                            const fileUrl = fileData?.url
                            const fileName = fileData?.name
                            const isImage = fileUrl?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i)
                            const isUploading = fileUploading === colId

                            const handleFileUpload = async (
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => {
                              const selectedFile = e.target.files?.[0]
                              if (!selectedFile) return

                              const nameArr = selectedFile.name.split(".")
                              const fileExt = nameArr[nameArr.length - 1]
                              if (BLOCKED_EXTENSIONS.includes(fileExt)) {
                                toast({
                                  title: translate("Error"),
                                  description: translate("Please select a valid file!"),
                                  variant: "error",
                                })
                                return
                              }

                              const formData = new FormData()
                              formData.append("files", selectedFile)

                              setFileUploading(colId)
                              try {
                                const response = await axios.post(
                                  `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`,
                                  formData,
                                  {
                                    headers: {
                                      accept: "*/*",
                                      "Content-Type": "multipart/form-data",
                                    },
                                  },
                                )
                                const uploadedFile = Array.isArray(response.data)
                                  ? response.data[0]
                                  : response.data
                                if (uploadedFile?.id) {
                                  updateValue(String(uploadedFile.id))
                                  setFileUploads((prev) => ({
                                    ...prev,
                                    [colId]: {
                                      url: uploadedFile.file || "",
                                      name: selectedFile.name,
                                    },
                                  }))
                                }
                              } catch (error: any) {
                                toast({
                                  title: translate("Error"),
                                  description:
                                    error?.response?.data?.message ||
                                    translate("Failed to upload file"),
                                  variant: "error",
                                })
                              } finally {
                                setFileUploading(null)
                              }

                              const ref = fileInputRefs.current[colId]
                              if (ref) ref.value = ""
                            }

                            return (
                              <div className='space-y-2' key={colId}>
                                <Label className='capitalize'>{translate(col.name)}</Label>
                                <input
                                  type='file'
                                  className='hidden'
                                  ref={(el) => {
                                    fileInputRefs.current[colId] = el
                                  }}
                                  onChange={handleFileUpload}
                                />
                                {isUploading ? (
                                  <div className='flex h-9 items-center gap-2 rounded-md border px-3'>
                                    <Loader className='h-4 w-4 animate-spin text-primary' />
                                    <span className='text-sm text-muted-foreground'>
                                      {translate("Uploading")}...
                                    </span>
                                  </div>
                                ) : fileUrl ? (
                                  <div className='flex h-9 items-center gap-2 rounded-md border px-3'>
                                    {isImage ? (
                                      <img
                                        src={fileUrl}
                                        alt={fileName || "file"}
                                        className='h-6 w-6 rounded border object-cover'
                                      />
                                    ) : (
                                      <FileText size={14} className='text-primary' />
                                    )}
                                    <span className='max-w-[120px] truncate text-sm'>
                                      {fileName}
                                    </span>
                                    <Eye
                                      size={14}
                                      className='cursor-pointer text-primary hover:text-primary/80'
                                      onClick={() => setPreviewColId(colId)}
                                    />
                                    <X
                                      size={14}
                                      className='cursor-pointer text-destructive'
                                      onClick={() => {
                                        updateValue("")
                                        setFileUploads((prev) => {
                                          const next = { ...prev }
                                          delete next[colId]
                                          return next
                                        })
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <Button
                                    type='button'
                                    variant='outline'
                                    className='w-full justify-start font-normal'
                                    onClick={() => fileInputRefs.current[colId]?.click()}
                                  >
                                    <Upload size={14} className={cn(isHebrew ? "ml-2" : "mr-2")} />
                                    {translate("Upload")}
                                  </Button>
                                )}

                                {/* File Preview */}
                                <Dialog
                                  open={previewColId === colId}
                                  onOpenChange={() => setPreviewColId(null)}
                                >
                                  <DialogContent className='max-w-3xl'>
                                    <DialogTitle>{fileName}</DialogTitle>
                                    {isImage && fileUrl ? (
                                      <img
                                        src={fileUrl}
                                        alt={fileName || "preview"}
                                        className='max-h-[80vh] w-full rounded object-contain'
                                      />
                                    ) : (
                                      <div className='flex flex-col items-center gap-4 py-8'>
                                        <FileText size={48} className='text-primary' />
                                        <a
                                          href={fileUrl}
                                          target='_blank'
                                          rel='noopener noreferrer'
                                          className='text-primary hover:underline'
                                        >
                                          {translate("Download")} {fileName}
                                        </a>
                                      </div>
                                    )}
                                  </DialogContent>
                                </Dialog>
                              </div>
                            )
                          }

                          // Text, Integer, Decimal
                          return (
                            <div className='space-y-2' key={colId}>
                              <Label className='capitalize'>{translate(col.name)}</Label>
                              <Input
                                type={
                                  colType === "integer" || colType === "decimal" ? "number" : "text"
                                }
                                step={colType === "decimal" ? "any" : undefined}
                                value={colValue}
                                onChange={(e) => updateValue(e.target.value)}
                                placeholder={translate(col.name)}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <Button className='mt-4 w-full' type='submit' disabled={addProjectMutation.isPending}>
                {translate("Save Project")}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AddProjectModal
